"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inventory() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: linkRef,
    isVisible: linkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    label: "Real-time Sync"
  }, {
    label: "OTA Integration"
  }, {
    label: "Parity Control"
  }];
  return <section data-section-id="4890" id="inventorysynchronizationprocess" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.2 0 0 0 0 0.15 0 0 0 0 0.1 0 0 0 0.4 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 py-32 md:py-40 lg:py-[200px]">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="lg:col-span-7">
            {}
            <div ref={eyebrowRef} className={`mb-12 transition-all duration-700 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-default text-xs tracking-[0.2em] uppercase text-secondary">
                — 01 · Inventory Synchronization
              </span>
            </div>

            {}
            <h2 ref={headlineRef} className={`font-default font-bold leading-[1.05] tracking-tight transition-all duration-1000 ease-out text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] text-primary ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              Seamless inventory control for{" "}
              <em className="italic font-default text-secondary">
                maximum revenue
              </em>
              .
            </h2>

            {}
            <p ref={paragraphRef} className={`mt-12 max-w-xl font-default text-base leading-[1.8] transition-all duration-1000 delay-150 ease-out text-gray-700 ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="float-left mr-3 font-default font-bold leading-[0.85] mt-1 text-secondary" style={{
              fontSize: "5.5rem"
            }}>
                A
              </span>
              void overbookings and missed opportunities with our automated inventory synchronization. We bridge the gap between your property management system and global OTAs, ensuring your availability is always accurate, updated in real-time, and optimized for high-demand periods.
            </p>

            {}
            <div ref={statsRef} className={`mt-16 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0 transition-all duration-1000 delay-300 ease-out ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {stats.map((stat, i) => <div data-index={i} key={i} className="flex items-center">
                  {i > 0 && <div className="hidden sm:block h-px w-8 mx-4 bg-gray-300" aria-hidden="true" />}
                  <span className="font-default text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-gray-600">
                    {stat.label}
                  </span>
                </div>)}
            </div>

            {}
            <div ref={linkRef} className={`mt-12 transition-all duration-1000 delay-500 ease-out ${linkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Link to="/OTARevenueManagementListingsPage" className="group inline-flex items-center gap-3 font-default text-xs tracking-[0.2em] uppercase border-b pb-1 transition-colors text-secondary border-secondary/40">
                <span>Explore OTA strategies</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-5">
            <div ref={imageRef} className="relative lg:-mt-10">
              <div className="relative overflow-hidden rounded-lg shadow-lg" style={{
              aspectRatio: "3 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_inventory_management.webp" alt="Digital dashboard showing real-time hotel inventory and revenue analytics" loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-[1400ms] ease-out" style={{
                clipPath: imageVisible ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)",
                transform: imageVisible ? "translateY(0)" : "translateY(40px)"
              }} />
              </div>

              {}
              <div className={`mt-5 transition-all duration-1000 delay-700 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
                <span className="font-default text-[10px] tracking-[0.2em] uppercase text-secondary">
                  — Fig. 01 · Revenue Dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}